import {
  collection,
  doc,
  addDoc,
  updateDoc,
  getDoc,
  setDoc,
  query,
  where,
  getDocs,
  serverTimestamp,
} from "firebase/firestore";

import { Collections } from "./constants";

/**
 * @typedef { import('./db-models').NoSQLGoal } NoSQLGoal
 * @typedef { import('./db-models').NoSQLGoalWeek } NoSQLGoalWeek
 */

/**
 * @param {import('firebase/firestore').Firestore} db The firestore database
 * @param {object} functions The functions adapter for callable functions
 * @returns {object} The goals collection access functions
 */
export default function goalsCollectionFns(db, functions) {
  /**
   * @param {string} userId The user id
   * @param {string} weekOf The Monday date key (yyyy-MM-dd)
   * @param {string} [organizationId] The organization to filter by
   * @returns {Promise<NoSQLGoal[]>} A list of goals for that week
   */
  async function listGoalsForWeek(userId, weekOf, organizationId) {
    try {
      const constraints = [
        where("userId", "==", userId),
        where("weekOf", "==", weekOf),
        where("deletedAt", "==", null),
      ];

      if (organizationId) {
        constraints.push(where("organizationId", "==", organizationId));
      }

      const q = query(collection(db, Collections.GOALS), ...constraints);

      const snapshot = await getDocs(q);
      const goals = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));

      // Sort by createdAt client-side to avoid requiring a composite index
      goals.sort((a, b) => {
        const aTime = a.createdAt?.toMillis?.() || 0;
        const bTime = b.createdAt?.toMillis?.() || 0;
        return aTime - bTime;
      });

      return goals;
    } catch (error) {
      console.error("Failed to list goals for week:", error);
      throw error;
    }
  }

  /**
   * @param {string} goalId The goal id
   * @returns {Promise<NoSQLGoal|null>} A goal or nothing
   */
  async function getGoal(goalId) {
    try {
      const docRef = doc(db, Collections.GOALS, goalId);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists() || docSnap.data().deletedAt) {
        return null;
      }

      return { id: docSnap.id, ...docSnap.data() };
    } catch (error) {
      console.error("Failed to get goal:", error);
      throw error;
    }
  }

  /**
   * @param {NoSQLGoal} goal The goal to save
   * @returns {Promise<string>} Returns the goal ID
   */
  async function saveGoal(goal) {
    const { id: goalId, ...goalData } = goal;

    try {
      if (goalId) {
        await updateDoc(doc(db, Collections.GOALS, goalId), goalData);
        return goalId;
      } else {
        const docRef = await addDoc(collection(db, Collections.GOALS), {
          ...goalData,
          createdAt: serverTimestamp(),
          deletedAt: null,
        });
        return docRef.id;
      }
    } catch (error) {
      console.error("Failed to save goal:", error);
      throw error;
    }
  }

  /**
   * @param {string} goalId The id of the goal to delete
   * @param {string} deletedBy The id of the user who is deleting
   * @returns {Promise<void>} Nothing
   */
  async function deleteGoal(goalId, deletedBy) {
    try {
      await updateDoc(doc(db, Collections.GOALS, goalId), {
        deletedAt: serverTimestamp(),
        deletedBy,
      });
    } catch (error) {
      console.error("Failed to delete goal:", error);
      throw error;
    }
  }

  /**
   * @param {string} userId The user id
   * @param {string} organizationId The organization id
   * @param {string} weekOf The Monday date key (yyyy-MM-dd)
   * @returns {Promise<NoSQLGoalWeek|null>} The week settings or null
   */
  async function getGoalWeek(userId, organizationId, weekOf) {
    try {
      const docId = `${userId}_${organizationId}_${weekOf}`;
      const docRef = doc(db, Collections.GOAL_WEEKS, docId);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        return null;
      }

      return { id: docSnap.id, ...docSnap.data() };
    } catch (error) {
      console.error("Failed to get goal week:", error);
      throw error;
    }
  }

  /**
   * @param {string} userId The user id
   * @param {string} organizationId The organization id
   * @param {string} weekOf The Monday date key (yyyy-MM-dd)
   * @returns {Promise<void>}
   */
  async function lockWeek(userId, organizationId, weekOf) {
    try {
      const docId = `${userId}_${organizationId}_${weekOf}`;
      const docRef = doc(db, Collections.GOAL_WEEKS, docId);
      await setDoc(
        docRef,
        {
          userId,
          organizationId,
          weekOf,
          lockedAt: serverTimestamp(),
        },
        { merge: true },
      );
    } catch (error) {
      console.error("Failed to lock week:", error);
      throw error;
    }
  }

  /**
   * @param {string} userId The user id
   * @param {string} organizationId The organization id
   * @param {string} weekOf The Monday date key (yyyy-MM-dd)
   * @returns {Promise<void>}
   */
  async function unlockWeek(userId, organizationId, weekOf) {
    try {
      const docId = `${userId}_${organizationId}_${weekOf}`;
      const docRef = doc(db, Collections.GOAL_WEEKS, docId);
      await setDoc(
        docRef,
        {
          userId,
          organizationId,
          weekOf,
          lockedAt: null,
        },
        { merge: true },
      );
    } catch (error) {
      console.error("Failed to unlock week:", error);
      throw error;
    }
  }

  /**
   * Mark that the user has reviewed the previous week's goals
   * @param {string} userId The user id
   * @param {string} organizationId The organization id
   * @param {string} weekOf The Monday date key (yyyy-MM-dd) of the CURRENT week
   * @returns {Promise<void>}
   */
  async function markPreviousWeekReviewed(userId, organizationId, weekOf) {
    try {
      const docId = `${userId}_${organizationId}_${weekOf}`;
      const docRef = doc(db, Collections.GOAL_WEEKS, docId);
      await setDoc(
        docRef,
        {
          userId,
          organizationId,
          weekOf,
          previousWeekReviewed: true,
        },
        { merge: true },
      );
    } catch (error) {
      console.error("Failed to mark previous week reviewed:", error);
      throw error;
    }
  }

  /**
   * Get all goals for multiple users in a week (for team view)
   * Uses callable function for server-side validation
   * @param {string[]} userIds Array of user IDs
   * @param {string} organizationId The organization id
   * @param {string} weekOf The Monday date key (yyyy-MM-dd)
   * @param {string} teamId The team ID (for validation)
   * @returns {Promise<NoSQLGoal[]>} A list of goals for all users
   */
  async function listTeamGoalsForWeek(userIds, organizationId, weekOf, teamId) {
    if (!userIds || userIds.length === 0) {
      return [];
    }

    try {
      // Call the secure function instead of querying directly
      const result = await functions.getTeamGoals({
        teamId,
        organizationId,
        weekOf,
        userIds,
      });

      return result.goals;
    } catch (error) {
      console.error("Failed to list team goals for week:", error);
      throw error;
    }
  }

  /**
   * Get goal week settings for multiple users (for team view)
   * Uses callable function for server-side validation
   * @param {string[]} userIds Array of user IDs
   * @param {string} organizationId The organization id
   * @param {string} weekOf The Monday date key (yyyy-MM-dd)
   * @param {string} teamId The team ID (for validation)
   * @returns {Promise<NoSQLGoalWeek[]>} Week settings for all users
   */
  async function listTeamGoalWeeks(userIds, organizationId, weekOf, teamId) {
    if (!userIds || userIds.length === 0) {
      return [];
    }

    try {
      // Call the secure function instead of querying directly
      const result = await functions.getTeamGoalWeeks({
        teamId,
        organizationId,
        weekOf,
        userIds,
      });

      return result.goalWeeks;
    } catch (error) {
      console.error("Failed to list team goal weeks:", error);
      throw error;
    }
  }

  return {
    listGoalsForWeek,
    getGoal,
    saveGoal,
    deleteGoal,
    getGoalWeek,
    lockWeek,
    unlockWeek,
    markPreviousWeekReviewed,
    listTeamGoalsForWeek,
    listTeamGoalWeeks,
  };
}
